import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import * as _ from 'lodash';
import { isEqual } from 'lodash';
import { AppSelections, AppTable, AppTableAction, AppTableColumn, AuthPermissions, CustomComponentConfig } from '../../_models/models';
import { getCellValueNode, getSmartSortDirection, getTruncatedCellValue } from './app-table.utils';
import { isCustomizableColumn } from '../../_common/app-table-column.helper';
import { NzResizeEvent } from 'ng-zorro-antd/resizable';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { ColumnPreferencesService } from '../../_services/column-preferences.service';

import { animate, state, style, transition, trigger, } from '@angular/animations';
import { Common } from '../../_common/common.helper';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomComponentHostDirective } from '../../_directives/custom-component-host.directive';
import {
  EXCHANGE_DEALS_EXTERNAL_ALERTS_SENTINEL_GROUP,
  EXCHANGE_DEALS_INTERNAL_ALERTS_SENTINEL_GROUP,
  EXTERNAL_ALERTS_SENTINEL_GROUP,
  INTERNAL_ALERTS_SENTINEL_GROUP
} from '../../_services/alerts/types/config.types';

/** The resizable columns of the current column set and the sum of their pixel widths (0 until every one has a pixel width). */
interface ColumnWidthTotals {
  columns: AppTableColumn[];
  resizable: AppTableColumn[];
  total: number;
}

/** Geometry captured once per drag so the guideline can follow the header cell being resized. */
interface ResizeGuideContext {
  thCell: HTMLElement;
  containerLeft: number;
  top: number;
  height: number;
}

@Component({
  selector: 'app-table',
  animations: [
    trigger('hideColumn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(300px)', offset: 1.0 }),
        animate('0.5s ease-in')
      ]),
      transition(':leave', [
        style({ opacity: .5, transform: 'translateY(-75%)',  offset: 0.3 }),
        animate('0.5s ease-out')
      ]),
    ]),
    trigger('freezeColumn', [
      state('freeze', style({
        color: '#FDD032'
      })),
      state('unfreeze', style({
        color: 'inherit'
      })),
    ]),
  ],
  templateUrl: './app-table.component.html',
  styleUrls: ['./app-table.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppTableComponent implements OnInit, AfterViewInit, OnDestroy {

  readonly MIN_COL_WIDTH = 72;
  readonly MAX_COL_WIDTH = 2000;

  cellTooltipTitle = '';
  isCellTooltipVisible = false;

  private hasResizedColumns = false;
  private tooltipCell: HTMLElement;
  private tooltipHideTimer: ReturnType<typeof setTimeout>;
  private resizeSuppressionTimer: ReturnType<typeof setTimeout>;
  private remeasureTimer: ReturnType<typeof setTimeout>;
  private cachedContainerWidth: number = null;
  private containerResizeObserver: ResizeObserver;
  private cachedColumnWidthTotals: ColumnWidthTotals;
  private readonly hideCellTooltipOnScroll = () => {
    if (this.tooltipCell || this.isCellTooltipVisible) {
      this.hideCellTooltip();
    }
  };
  /**
   * A drag that releases on the header body (not on the handle) makes the browser fire a click on the
   * th, which would flip ng-zorro's internal sort order. Swallow it in the capture phase while a resize
   * is in flight so the sort arrows stay where they were.
   */
  private readonly swallowHeaderClickWhileResizing = (event: Event) => {
    if (this.isResizingColumn && (event.target as HTMLElement)?.closest?.('th.app-table-resizable-col')) {
      event.stopPropagation();
    }
  };
  private readonly TOOLTIP_HIDE_DELAY_MS = 100;
  private readonly RESIZE_SORT_SUPPRESSION_MS = 200;
  private pendingWidths = new Map<string | number, string>();
  private isResizingColumn = false;
  private hasDragMoved = false;
  private structuralColumnsWidth: number = null;
  private actionsColumnContentWidth = 0;
  private resizeGuideContext: ResizeGuideContext = null;
  private scrollConfigCache: { base: object; x: string; value: object };
  private readonly RESIZE_GUIDE_WIDTH_PX = 2;

  @ViewChild('appTable') appTableEl;
  @ViewChildren('thCell', { read: ElementRef }) thCells: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('resizeGuide') resizeGuideEl: ElementRef<HTMLElement>;
  @ViewChild('cellTooltipAnchor', { read: ElementRef }) cellTooltipAnchorEl: ElementRef<HTMLElement>;
  @ViewChild('cellTooltipAnchor', { read: NzTooltipDirective }) cellTooltipAnchor: NzTooltipDirective;
  @ViewChildren(CustomComponentHostDirective) customComponentHosts!: CustomComponentHostDirective[];

  @Input() tableTitle: string;
  @Input() tableType: string;
  @Input() innerTableColumns: Array<any>;
  @Input()
  set isInnerTable(isInnerTable: boolean) {
    this._isInnerTable = isInnerTable;
    this.reapplyColumnPreferences();
  }
  get isInnerTable(): boolean {
    return this._isInnerTable;
  }
  @Input()
  set table(table: AppTable) {
    this._table = table;
    this.reapplyColumnPreferences();
  }
  get table(): AppTable {
    return this._table;
  }

  @Input() selections: AppSelections;
  @Input() authPermissions: AuthPermissions;
  @Input() scrollConfig: any = { x: 'inherit' };
  @Input() isExternal = false;
  @Input() idProp: string;
  @Input() hasAccess: string;
  @Input() exportCSV = false;
  @Input() hasFooter = false;
  @Input() tableTotalsData: any;
  @Input() totalRows: number;
  @Input() isTableLoading: boolean;
  @Input() isDownloading: boolean;
  @Input() isPaginated = true;
  @Input() hasSizeChanger = true;
  @Input() columnGroupLabels: Record<string, string> = {};

  @Output() trafficSourcesAction: EventEmitter<any> = new EventEmitter();
  @Output() adServerTechAction: EventEmitter<any> = new EventEmitter();
  @Output() termsOfServiceAction: EventEmitter<any> = new EventEmitter();
  @Output() emailTemplateAction: EventEmitter<any> = new EventEmitter();
  @Output() inventoryPackagesAction: EventEmitter<any> = new EventEmitter();
  @Output() kvpValuesAction: EventEmitter<any> = new EventEmitter();
  @Output() inventoryPackagesGeoAction: EventEmitter<any> = new EventEmitter();
  @Output() dealsAction: EventEmitter<any> = new EventEmitter();
  @Output() exchangeDealsAction: EventEmitter<any> = new EventEmitter();
  @Output() exchangeForecastsAction: EventEmitter<any> = new EventEmitter();
  @Output() exchangePartnersAction: EventEmitter<any> = new EventEmitter();
  @Output() universalMediaListsAction: EventEmitter<any> = new EventEmitter();
  @Output() universalMediaListsDomainAction: EventEmitter<any> = new EventEmitter();
  @Output() universalMediaListAssociatedDealsAction: EventEmitter<any> = new EventEmitter();
  @Output() publishersAction: EventEmitter<any> = new EventEmitter();
  @Output() placementsAction: EventEmitter<any> = new EventEmitter();
  @Output() domainBundlesAction: EventEmitter<any> = new EventEmitter();
  @Output() domainBundlesChangesAction: EventEmitter<any> = new EventEmitter();
  @Output() badvAction: EventEmitter<{ row: any; id: number | string; action: string }> = new EventEmitter();
  @Output() rolesAction: EventEmitter<any> = new EventEmitter();
  @Output() usersAction: EventEmitter<any> = new EventEmitter();
  @Output() billingHistoryAction: EventEmitter<any> = new EventEmitter();
  @Output() alertRowAction: EventEmitter<{ row: any; action: string }> = new EventEmitter();
  @Output() scheduledReportsAction: EventEmitter<any> = new EventEmitter();

  @Output() tablePageChanged: EventEmitter<any> = new EventEmitter();
  @Output() tableSortChanged: EventEmitter<any> = new EventEmitter();
  @Output() selectionsChanged: EventEmitter<any> = new EventEmitter();
  @Output() pageIndexChanged: EventEmitter<any> = new EventEmitter();
  @Output() exportClicked: EventEmitter<any> = new EventEmitter();

  @Output() rowAction: EventEmitter<any> = new EventEmitter();
  sortDirection: 'asc' | 'desc' | null | undefined;

  confirmId: number | string;
  columnsProps: string[];
  totalClientSideRows: number;
  frozenColumnsLeftValues = [];

  private _tableData: any;
  private _tableColumns: AppTableColumn[];
  private _rawColumns: AppTableColumn[];
  private _columnsStorageKey: string;
  private _table: AppTable;
  private _isInnerTable = false;
  private readonly COLUMN_PICKER_CELL_WIDTH_PX = 48;

  @Input()
  set tableData(tableData: any[]) {
    if (!isEqual(this._tableData, tableData)) {
      this._tableData = tableData;
      if (this._tableData) {
        this.totalClientSideRows = this._tableData.length;
        setTimeout(() => {
          this.captureStructuralColumnsWidth();
          this.calcStickyWidthLeft();
          this._tableData?.forEach((row, idx) => {
            if (row.isExpanded && row.expandRowComponent) {
              this.loadCustomComponent(row, idx, 'expandRowComponent');
            }
            if (row.trComponent) {
              this.loadCustomComponent(row, idx, 'trComponent');
            }
          });
        }, 100);
      }
    }
  }
  get tableData(): any[] {
    return this._tableData;
  }

  @Input()
  set tableColumns(tableColumns: AppTableColumn[]) {
    this._rawColumns = AppTableComponent.withDefaultValueFormatters(tableColumns);
    this._tableColumns = this.columnPrefs.applyToColumns(this._rawColumns, this.effectiveStorageKey);
    this.refreshResizedFlag();
    if (this._tableColumns) {
      this.scheduleRemeasure(100);
    }
  }
  get tableColumns(): AppTableColumn[] {
    return this._tableColumns;
  }

  // Column types whose app-table.component.html render path invokes column.valueFormatter(...).
  // A missing formatter here throws (FUS-4496); default to a safe no-op/identity per render path.
  private static readonly DEFAULT_VALUE_FORMATTERS: Partial<Record<string, (...args: any[]) => any>> = {
    'custom-function': (value: any) => value,
    'custom-function-html': (value: any) => value,
    'link': () => null,
    'toggle-deal': () => null,
    'text-with-tooltip': () => null,
    'link-or-tag': () => null,
    'moreTag': () => null,
  };

  private static withDefaultValueFormatters(columns: AppTableColumn[]): AppTableColumn[] {
    if (!columns) {
      return columns;
    }
    return columns.map(col => {
      const defaultFormatter = AppTableComponent.DEFAULT_VALUE_FORMATTERS[col.type];
      return defaultFormatter && typeof col.valueFormatter !== 'function'
        ? { ...col, valueFormatter: defaultFormatter }
        : col;
    });
  }

  @Input()
  set columnsStorageKey(storageKey: string) {
    this._columnsStorageKey = storageKey;
    this.reapplyColumnPreferences();
  }
  get columnsStorageKey(): string {
    return this._columnsStorageKey;
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    this.confirmId = null;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    public common: Common,
    private router: Router,
    private route: ActivatedRoute,
    private columnPrefs: ColumnPreferencesService,
    private renderer: Renderer2,
    private hostEl: ElementRef<HTMLElement>
  ) {}

  get actionsColumnRightOffset(): string {
    return this.hasColumnPicker ? this.columnPickerCellWidth : '0px';
  }

  get hasColumnPicker(): boolean {
    return !!this.columnsStorageKey && !!this._tableColumns?.some(column => column.columnGroup);
  }

  get columnPickerCellWidth(): string {
    return `${this.COLUMN_PICKER_CELL_WIDTH_PX}px`;
  }

  get effectiveStorageKey(): string {
    if (this.isInnerTable) {
      return '';
    }
    return this._columnsStorageKey || (this._table?.id ? `appTable:${this._table.id}` : '');
  }

  get actionsColumnWidth(): string {
    return this.actionsColumnContentWidth ? `${this.actionsColumnContentWidth}px` : null;
  }

  get hasActionsColumn(): boolean {
    return (this._tableColumns || []).some(column => column.type === 'actions');
  }

  get isColumnWidthModeActive(): boolean {
    return (this.hasResizedColumns || this.isResizingColumn) && this.structuralColumnsWidth !== null && this.totalColumnsWidth > 0;
  }

  get tableScrollConfig(): any {
    if (!this.isColumnWidthModeActive) {
      return this.scrollConfig;
    }
    const x = `${Math.max(this.requiredTableWidth, this.containerWidth)}px`;
    if (this.scrollConfigCache?.x !== x || this.scrollConfigCache.base !== this.scrollConfig) {
      this.scrollConfigCache = { base: this.scrollConfig, x, value: { ...this.scrollConfig, x } };
    }
    return this.scrollConfigCache.value;
  }

  get fillerColumnWidth(): string {
    if (this.structuralColumnsWidth === null) {
      return null;
    }
    return `${Math.max(0, Math.round(this.containerWidth - this.requiredTableWidth))}px`;
  }

  showFullValueIfTruncated(event: MouseEvent): void {
    if (this.isResizingColumn) {
      return;
    }
    const cell = (event.target as HTMLElement)?.closest<HTMLElement>('th, td');
    if (cell === this.tooltipCell) {
      clearTimeout(this.tooltipHideTimer);
      return;
    }
    const fullValue = cell && !this.hasOwnTooltip(cell) ? getTruncatedCellValue(cell) : null;
    if (!fullValue) {
      this.hideCellTooltip();
      return;
    }
    this.showCellTooltip(cell, fullValue);
  }

  private hasOwnTooltip(cell: HTMLElement): boolean {
    if (getCellValueNode(cell)?.hasAttribute('nz-tooltip')) {
      return true;
    }
    const column = this._tableColumns?.find(col => String(col.id) === cell.dataset.columnId);
    return !!column?.tooltip;
  }

  hideCellTooltip(): void {
    clearTimeout(this.tooltipHideTimer);
    this.tooltipHideTimer = setTimeout(() => {
      this.tooltipCell = null;
      this.isCellTooltipVisible = false;
      this.detectChangesSafely();
    }, this.TOOLTIP_HIDE_DELAY_MS);
  }

  private showCellTooltip(cell: HTMLElement, fullValue: string): void {
    clearTimeout(this.tooltipHideTimer);
    const anchor = this.cellTooltipAnchorEl?.nativeElement;
    const container = anchor?.parentElement?.getBoundingClientRect();
    if (!anchor || !container) {
      return;
    }
    this.tooltipCell = cell;
    const cellRect = cell.getBoundingClientRect();
    this.renderer.setStyle(anchor, 'left', `${cellRect.left - container.left}px`);
    this.renderer.setStyle(anchor, 'top', `${cellRect.top - container.top}px`);
    this.renderer.setStyle(anchor, 'width', `${cellRect.width}px`);
    this.renderer.setStyle(anchor, 'height', `${cellRect.height}px`);
    this.cellTooltipTitle = fullValue;
    this.isCellTooltipVisible = true;
    this.detectChangesSafely();
    this.cellTooltipAnchor?.updatePosition();
  }

  onColumnResizeStart(): void {
    this.isResizingColumn = true;
    this.hasDragMoved = false;
    this.hideCellTooltip();
    this.captureStructuralColumnsWidth();
    this.pendingWidths = this.resolveColumnWidths();
  }

  /**
   * The width each column has on screen right now, so the first drag keeps every other column where it was.
   * The configured pixel width is only a fallback for a column that has no laid-out header cell to measure.
   */
  private resolveColumnWidths(columns: AppTableColumn[] = this.resizableColumns): Map<string | number, string> {
    const cellsByColumnId = new Map((this.thCells?.toArray() ?? []).map(cell => [cell.nativeElement.dataset.columnId, cell]));
    const widths = new Map<string | number, string>();
    columns.forEach(column => {
      const cell = cellsByColumnId.get(String(column.id));
      const width = (cell && this.measuredCellWidth(cell)) || this.pixelColWidth(column);
      if (width) {
        widths.set(column.id, width);
      }
    });
    return widths;
  }

  onResetColumnWidth(column: AppTableColumn): void {
    this.columnPrefs.clearColumnWidth(this.effectiveStorageKey, column.id);
    this.reapplyColumnPreferences();
    this.detectChangesSafely();
    this.remeasureColumns();
  }

  onColumnResizing(column: AppTableColumn, { width }: NzResizeEvent): void {
    if (!this.hasDragMoved) {
      this.hasDragMoved = true;
      this.setColumnWidths(this.pendingWidths);
    }
    this.setColumnWidths(new Map([[column.id, `${Math.round(width)}px`]]), column.id);
    this.moveResizeGuide();
  }

  private pixelColWidth(column: AppTableColumn): string {
    return column.colWidth?.endsWith('px') ? column.colWidth : null;
  }

  /** Null when the cell has no layout yet (hidden tab, destroyed view), so a bogus minimum width is never stored. */
  private measuredCellWidth(cell: ElementRef<HTMLElement>): string | null {
    const width = Math.floor(cell.nativeElement.getBoundingClientRect().width);
    return width > 0 ? `${Math.max(this.MIN_COL_WIDTH, width)}px` : null;
  }

  private moveResizeGuide(): void {
    const guide = this.resizeGuideEl?.nativeElement;
    if (!guide) {
      return;
    }
    if (!this.resizeGuideContext) {
      this.resizeGuideContext = this.buildResizeGuideContext(guide);
      if (!this.resizeGuideContext) {
        return;
      }
      this.renderer.setStyle(guide, 'top', `${this.resizeGuideContext.top}px`);
      this.renderer.setStyle(guide, 'height', `${this.resizeGuideContext.height}px`);
      this.renderer.setStyle(guide, 'display', 'block');
    }
    const boundary = this.resizeGuideContext.thCell.getBoundingClientRect().right - this.resizeGuideContext.containerLeft;
    this.renderer.setStyle(guide, 'left', `${boundary - this.RESIZE_GUIDE_WIDTH_PX / 2}px`);
  }

  private buildResizeGuideContext(guide: HTMLElement): ResizeGuideContext {
    const thCell = this.hostEl.nativeElement.querySelector<HTMLElement>('th.nz-resizable-resizing');
    const container = guide.parentElement?.getBoundingClientRect();
    const table = thCell?.closest('.ant-table-container')?.getBoundingClientRect();
    const header = thCell?.closest('thead')?.getBoundingClientRect();
    if (!thCell || !container || !table || !header) {
      return null;
    }
    return {
      thCell,
      containerLeft: container.left,
      top: header.bottom - container.top,
      height: table.bottom - header.bottom,
    };
  }

  private detectChangesSafely(): void {
    if (this.cdr && !this.cdr['destroyed']) {
      this.cdr.detectChanges();
    }
  }

  private hideResizeGuide(): void {
    this.resizeGuideContext = null;
    const guide = this.resizeGuideEl?.nativeElement;
    if (guide) {
      this.renderer.setStyle(guide, 'display', 'none');
    }
  }

  trackByColumnId(_index: number, column: AppTableColumn): string | number {
    return column.id;
  }

  onColumnResized(column: AppTableColumn, { width }: NzResizeEvent): void {
    this.hideResizeGuide();
    clearTimeout(this.resizeSuppressionTimer);
    this.resizeSuppressionTimer = setTimeout(() => this.isResizingColumn = false, this.RESIZE_SORT_SUPPRESSION_MS);
    if (!this.hasDragMoved) {
      return;
    }
    this.pendingWidths.set(column.id, `${Math.round(width)}px`);
    this.setColumnWidths(this.pendingWidths, column.id);
    this.columnPrefs.persistColumnWidths(this.effectiveStorageKey, this._tableColumns);
    this.refreshResizedFlag();
    this.calcStickyWidthLeft();
  }

  private setColumnWidths(widths: Map<string | number, string>, resizedId?: string | number): void {
    this._tableColumns = this._tableColumns.map(col =>
      widths.has(col.id)
        ? { ...col, colWidth: widths.get(col.id), maxWidth: col.id === resizedId ? undefined : col.maxWidth }
        : col
    );
    this.detectChangesSafely();
  }

  private get resizableColumns(): AppTableColumn[] {
    return this.columnWidthTotals.resizable;
  }

  private get totalColumnsWidth(): number {
    return this.columnWidthTotals.total;
  }

  private get columnWidthTotals(): ColumnWidthTotals {
    if (this.cachedColumnWidthTotals?.columns !== this._tableColumns) {
      const resizable = (this._tableColumns || []).filter(col => !col.isHidden && isCustomizableColumn(col));
      const widths = resizable.map(col => parseInt(this.pixelColWidth(col), 10)).filter(width => width > 0);
      const total = widths.length === resizable.length ? widths.reduce((sum, width) => sum + width, 0) : 0;
      this.cachedColumnWidthTotals = { columns: this._tableColumns, resizable, total };
    }
    return this.cachedColumnWidthTotals;
  }

  private get containerWidth(): number {
    return this.cachedContainerWidth ?? this.measureContainerWidth();
  }

  private measureContainerWidth(): number {
    return this.hostEl.nativeElement.querySelector('.table-container')?.clientWidth || 0;
  }

  private get requiredTableWidth(): number {
    return this.totalColumnsWidth + (this.structuralColumnsWidth || 0) + this.pickerCellWidth;
  }

  private get pickerCellWidth(): number {
    return this.hasColumnPicker ? this.COLUMN_PICKER_CELL_WIDTH_PX : 0;
  }

  private captureStructuralColumnsWidth(): void {
    const headerRow = this.thCells?.first?.nativeElement.closest('tr');
    if (!headerRow) {
      return;
    }
    const resizableCells = new Set<Element>(this.thCells.map(cell => cell.nativeElement));
    const widestActionsCellWidth = this.widestActionsCellWidth();
    this.actionsColumnContentWidth = widestActionsCellWidth;
    this.structuralColumnsWidth = Array.from(headerRow.children)
      .filter(cell => !resizableCells.has(cell) &&
        !cell.classList.contains('app-table-filler-th') &&
        !cell.classList.contains('app-table-column-picker-th'))
      .reduce((sum, cell) => sum + this.structuralCellWidth(cell, widestActionsCellWidth), 0);
  }

  private structuralCellWidth(cell: Element, widestActionsCellWidth: number): number {
    const width = Math.max(cell.getBoundingClientRect().width, cell.scrollWidth);
    return cell.classList.contains('app-table-actions-th') ? Math.max(width, widestActionsCellWidth) : width;
  }

  private widestActionsCellWidth(): number {
    const ownWrapper = this.hostEl.nativeElement.querySelector('#app-table');
    const actionsCells = Array.from(this.hostEl.nativeElement.querySelectorAll<HTMLElement>(
      'thead th.app-table-actions-th, tbody td.app-table-actions-col'))
      .filter(cell => cell.closest('#app-table') === ownWrapper);
    const measuredContents = new Set<string>();
    let widest = 0;
    actionsCells.forEach(cell => {
      const contentKey = `${cell.tagName}:${cell.children.length}:${cell.textContent}`;
      if (measuredContents.has(contentKey)) {
        return;
      }
      measuredContents.add(contentKey);
      widest = Math.max(widest, this.actionsCellRequiredWidth(cell));
    });
    return widest;
  }

  private actionsCellRequiredWidth(cell: HTMLElement): number {
    const contentRange = cell.ownerDocument.createRange();
    contentRange.selectNodeContents(cell);
    const cellStyles = getComputedStyle(cell);
    return Math.ceil(contentRange.getBoundingClientRect().width + parseFloat(cellStyles.paddingLeft) + parseFloat(cellStyles.paddingRight));
  }

  private reapplyColumnPreferences(): void {
    if (!this._rawColumns) {
      return;
    }
    this._tableColumns = this.columnPrefs.applyToColumns(this._rawColumns, this.effectiveStorageKey);
    this.refreshResizedFlag();
  }

  private refreshResizedFlag(): void {
    this.hasResizedColumns = this.columnPrefs.hasStoredWidths(this.effectiveStorageKey);
  }

  ngOnInit() {
    if (!this.tableData) {
      return;
    }
    this.columnsProps = _.map(this.tableColumns, 'prop');
  }

  ngAfterViewInit() {
    this.observeContainerWidth();
    this.hostEl.nativeElement.addEventListener('scroll', this.hideCellTooltipOnScroll, true);
    this.hostEl.nativeElement.addEventListener('click', this.swallowHeaderClickWhileResizing, true);
    if (!this.tableData || this.tableData.length === 0) {
      return;
    }
    setTimeout(() => this.calcStickyWidthLeft(), 0);
  }

  private observeContainerWidth(): void {
    const container = this.hostEl.nativeElement.querySelector('.table-container');
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.containerResizeObserver = new ResizeObserver(() => {
      this.cachedContainerWidth = this.measureContainerWidth();
      this.detectChangesSafely();
    });
    this.containerResizeObserver.observe(container);
  }

  hasPermissions() {
    if (this.authPermissions && Object.prototype.hasOwnProperty.call(this.authPermissions, 'showActions')) {
      return this.authPermissions.showActions && Object.values(this.authPermissions).indexOf(true) > -1;
    } else {
      return this.authPermissions && Object.values(this.authPermissions).indexOf(true) > -1;
    }
  }

  onPageIndexChanged(event) {
    this.table.pageIndex = event;
    this.tablePageChanged.emit({ ...this.table });
  }

  onPageSizeChanged(event) {
    this.table.pageSize = event;
    this.table.pageIndex = 1;
    this.tablePageChanged.emit({ ...this.table });
  }

  onTableSortChange(sortEvent: { key: string; value: 'ascend' | 'descend' | null }): void {
    if (this.isResizingColumn) {
      return;
    }
    if (!this.table) return;

    if (this.tableType === 'clientSide') {
      this.handleClientSideSort(sortEvent);
    } else {
      this.handleServerSideSort(sortEvent);
    }
  }

  onColumnsChange(columns: AppTableColumn[]): void {
    this._tableColumns = columns;
    this.refreshResizedFlag();
    this.scheduleRemeasure(0);
  }

  private scheduleRemeasure(delayMs: number): void {
    clearTimeout(this.remeasureTimer);
    this.remeasureTimer = setTimeout(() => this.remeasureColumns(), delayMs);
  }

  private remeasureColumns(): void {
    this.captureStructuralColumnsWidth();
    this.measureMissingWidths();
    this.calcStickyWidthLeft();
  }

  /** Once any width is stored, pin every resizable column still without a pixel width to its on-screen width. */
  private measureMissingWidths(): void {
    if (!this.hasResizedColumns) {
      return;
    }
    const unmeasured = this.resizableColumns.filter(column => !this.pixelColWidth(column));
    if (!unmeasured.length) {
      return;
    }
    const widths = this.resolveColumnWidths(unmeasured);
    if (widths.size) {
      this.setColumnWidths(widths);
      this.columnPrefs.persistColumnWidths(this.effectiveStorageKey, this._tableColumns);
    }
  }

  private handleClientSideSort(sortEvent: { key: string; value: 'ascend' | 'descend' | null }): void {
    const { key, value } = sortEvent;
    if (value) {
      this.sortDirection = value === 'ascend' ? 'asc' : 'desc';
      if (this._tableData) {
        this._tableData = _.orderBy(this._tableData, [val => {
          const sortVal = _.get(val, key);
          return (typeof sortVal === 'string' || sortVal instanceof String) ? sortVal.toLowerCase() : sortVal;
        }], [this.sortDirection]);
      }
    } else {
      this.sortDirection = null;
    }
    this.cdr.markForCheck();
  }

  private handleServerSideSort(sortEvent: { key: string; value: 'ascend' | 'descend' | null }): void {
    const tableStateToEmit = { ...this.table };
    let nextSortDirection: 'ASC' | 'DESC' | null = null;
    const currentSortKey = this.table.sortBy;
    const currentSortDirection = this.table.sortDirection;

    if (currentSortKey === sortEvent.key) {
      if (currentSortDirection === 'ASC') {
        nextSortDirection = 'DESC';
      } else {
        nextSortDirection = 'ASC';
      }
    } else {
      const clickedColumn = this.tableColumns.find(col =>
        col.prop === sortEvent.key || col.sortingProp === sortEvent.key
      );
      if (clickedColumn) {
        nextSortDirection = getSmartSortDirection(clickedColumn);
      } else {
        nextSortDirection = this.table.initialSortDirection || 'ASC';
      }
    }

    tableStateToEmit.sortBy = sortEvent.key;
    tableStateToEmit.sortDirection = nextSortDirection;

    this.tableSortChanged.emit(tableStateToEmit);
    this.cdr.markForCheck();
  }

  onItemsSelectionsChange(changedRow = null, changeAction = null) {
    this.selectionsChanged.emit({ selections: this.selections, changedRow: changedRow, changeAction: changeAction });
  }

  selectAll() {
    this.selections.excludedItems = [];
    this.selections.selectedItems = [];

    if (this.tableType === 'clientSide') {
      const isNotSelectAll = !this.selections.isSelectAll;
      const areExcludedItemsNotEmpty = this.selections.excludedItems.length > 0;

      this.selections.isSelectAll = isNotSelectAll || areExcludedItemsNotEmpty;
    }

    this.onItemsSelectionsChange();
  }


  isRowSelected(row, idProp: string) {
    if (this.selections.isSelectAll) {
      return !this.isRowExcluded(row, idProp);
    }
    return _.findIndex(this.selections.selectedItems, { [idProp]: row[idProp] }) > -1;
  }

  isRowExcluded(row, idProp: string) {
    return _.findIndex(this.selections.excludedItems, { [idProp]: row[idProp] }) > -1;
  }

  updateExcludedItems(row, idProp: string) {
    if (this.isRowExcluded(row, idProp)) {
      _.remove(this.selections.excludedItems, { [idProp]: row[idProp] });
    } else {
      this.selections.excludedItems.push(row);
    }
  }

  updateSelectedObjects(row, idProp: string) {
    if (this.selections.isSelectAll) {
      this.updateExcludedItems(row, idProp);
    } else {
      const isSelected = this.isRowSelected(row, idProp);
      if (isSelected) {
        _.remove(this.selections.selectedItems, { [idProp]: row[idProp] });
      } else {
        this.selections.selectedItems.push(row);
      }
    }

    if (this.tableType === 'clientSide') {
    // check if user unselected one by one all rows in all pages
      if (this.selections.selectedItems.length === 0 && this.selections.excludedItems.length === this.totalRows) {
        this.selections.isSelectAll = false;
        this.selections.excludedItems = [];
      }
      // check if user selected one by one all rows in all pages
      if (this.selections.selectedItems.length === this.totalRows) {
        this.selections.isSelectAll = true;
        this.selections.selectedItems = [];
      }
    }
    this.onItemsSelectionsChange(row);
  }

  onShowConfirmDialog(rowId: number | string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.confirmId = rowId;
    this.cdr.markForCheck();
  }

  onRowAction(row: any, id: number | string, rowIndex: number, actionId: string, data: any = null) {
    const updateRow = _.cloneDeep(row);
    switch (actionId) {
      case 'trafficSourcesView':
      case 'trafficSourcesEdit':
      case 'trafficSourcesDelete':
        this.trafficSourcesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'adServerTechEdit':
      case 'adServerTechDelete':
        this.adServerTechAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'termsOfServiceView':
        this.termsOfServiceAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'emailTemplateView':
      case 'emailTemplateEdit':
        this.emailTemplateAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'inventoryPackagesEdit':
      case 'inventoryPackagesDelete':
      case 'inventoryPackageNameId':
      case 'inventoryPackageSelectForDeal':
      case 'inventoryPackageCopyName':
      case 'inventoryPackagesRunForecast':
        this.inventoryPackagesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'kvpValueDelete':
        this.kvpValuesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'dealEdit':
      case 'dealDuplicate':
      case 'dealCopyId':
      case 'dealStatusToggle':
        this.dealsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'exchangeDealEdit':
      case 'exchangeDealDuplicate':
        this.exchangeDealsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'exchangeForecastsActionDelete':
        this.exchangeForecastsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'exchangePartnersActionDelete':
      case 'exchangePartnersActionUpdateStatus':
        this.exchangePartnersAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'universalMediaListsActionEdit':
      case 'universalMediaListsActionDuplicate':
      case 'universalMediaListsActionArchive':
      case 'universalMediaListsActionUnarchive':
        this.universalMediaListsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'universalMediaListsDomainActionDelete':
        this.universalMediaListsDomainAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'universalMediaListAssociatedDealsActionRemove':
        this.universalMediaListAssociatedDealsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'publisherEdit':
      case 'publisherDelete':
      case 'publisherView':
        this.publishersAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'placementStatusToggle':
      case 'placementEdit':
      case 'placementDuplicate':
      case 'placementView':
        this.placementsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'domainBundleDelete':
      case 'domainBundlesIncludeSubDomains':
        this.domainBundlesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'cancelAction':
        this.domainBundlesChangesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'badvDelete':
        this.badvAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'usersView':
      case 'usersEdit':
      case 'usersDelete':
        this.usersAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'roleEdit':
      case 'roleDelete':
        this.rolesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'billPaymentDetails':
        this.billingHistoryAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'alertToggleAction':
      case INTERNAL_ALERTS_SENTINEL_GROUP:
      case EXTERNAL_ALERTS_SENTINEL_GROUP:
      case EXCHANGE_DEALS_INTERNAL_ALERTS_SENTINEL_GROUP:
      case EXCHANGE_DEALS_EXTERNAL_ALERTS_SENTINEL_GROUP:
      case 'alertDeleteAction':
        this.alertRowAction.emit({ row: updateRow, action: actionId });
        break;
      case 'scheduledReportEdit':
      case 'scheduledReportDelete':
      case 'scheduledReportToggleStatus':
        this.scheduledReportsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      default:
    }

    this.rowAction.emit({ row: updateRow, id: id, action: actionId, data: data });
  }

  onExportClicked($event) {
    this.exportClicked.emit($event);
  }

  lodashGet(obj, path, columnId = '') {
    let val = _.get(obj, path);
    if (columnId === 'dealsDefaultBidfloor' && val) {
      val = '$' + val;
    }
    return val;
  }

  calcStickyWidthLeft() {
    this.frozenColumnsLeftValues = [];
    let left = 0;
    this.tableColumns.forEach((col, idx) => {
      if (col.isFrozenCol) {
        this.frozenColumnsLeftValues[idx] = left;
        const width = _.get(this.appTableEl, `listOfNzThComponent._results[${idx}].cdr._view.parent.nodes[0].renderElement.clientWidth`);
        left += width;
      }
    });
    this.detectChangesSafely();
  }

  getStickyWidthLeft(idx: number) {
    if (this.frozenColumnsLeftValues[idx] >= 0) {
      return `${this.frozenColumnsLeftValues[idx]}px`;
    }
  }

  getHrefLink(link: string, id: number | string) {
    return link.replace('{{id}}', id.toString());
  }

  getDisplayNameFormatterValue(col) {
    return <AppTableColumn>col.displayNameFormatter(col);
  }

  resolveFormattedValue(column: AppTableColumn, fallback: any, ...args: any[]): any {
    return typeof column?.valueFormatter === 'function' ? column.valueFormatter(...args) : fallback;
  }

  routeIf(route: string[], openTab: boolean = false): void {
    if (!route) {
      return;
    }
    if (openTab) {
      const urlTree = this.router.createUrlTree(route, { relativeTo: this.route });
      const url = this.router.serializeUrl(urlTree);
      window.open(url, '_blank');
    } else {
      this.router.navigate(route, { relativeTo: this.route });
    }
  }

  loadCustomComponent(row, rowIndex, customComponentRowKey: string): void {
    const componentConfig: CustomComponentConfig = row[customComponentRowKey];
    const host =
      this.customComponentHosts.filter((node, idx) => idx === rowIndex)[0];
    if (componentConfig?.component) {
      host.componentConfig = componentConfig;
      if (componentConfig.data) {
        host.componentConfig.data = componentConfig.data;
      }
    }
  }

  getVisibleActions(row: any, actions: AppTableAction[]): AppTableAction[] {
    if (!actions) {
      return [];
    }

    return actions.filter(action => !action.condition || action.condition(row));
  }

  ngOnDestroy(): void {
    clearTimeout(this.tooltipHideTimer);
    clearTimeout(this.resizeSuppressionTimer);
    clearTimeout(this.remeasureTimer);
    this.hostEl.nativeElement.removeEventListener('scroll', this.hideCellTooltipOnScroll, true);
    this.hostEl.nativeElement.removeEventListener('click', this.swallowHeaderClickWhileResizing, true);
    this.containerResizeObserver?.disconnect();
    this.cdr.detach();
  }

  getMoreTagCounter(items: any[]): number {
    if (!items) return 0;
    return items.length - 1;
  }

  getListItemDisplayText(item: any, column: AppTableColumn): string {

    const displayTextStrategies = new Map([
      ['formatterOnly', (specItem: any, specColumn: AppTableColumn) =>
        this.processFormatterResult(specColumn.moreTag.listItemFormatter(specItem), specItem)],
      ['displayNameOnly', (specItem: any, specColumn: AppTableColumn) =>
        this.lodashGet(specItem, specColumn.moreTag.displayNameProp)],
      ['formatterAndDisplayName', (specItem: any, specColumn: AppTableColumn) => {
        const formatterResult = this.processFormatterResult(specColumn.moreTag.listItemFormatter(specItem), specItem);
        const displayNameResult = this.lodashGet(specItem, specColumn.moreTag.displayNameProp);
        return formatterResult && displayNameResult ? `${formatterResult} - ${displayNameResult}` : formatterResult || displayNameResult;
      }],
      ['default', (specItem: any, specColumn: AppTableColumn) =>
        this.lodashGet(specItem, specColumn.moreTag?.displayNameProp || '')]
    ]);

    const hasFormatter = !!column.moreTag?.listItemFormatter;
    const hasDisplayName = !!column.moreTag?.displayNameProp;

    let strategyKey = 'default';
    if (hasFormatter && !hasDisplayName) strategyKey = 'formatterOnly';
    else if (!hasFormatter && hasDisplayName) strategyKey = 'displayNameOnly';
    else if (hasFormatter && hasDisplayName) strategyKey = 'formatterAndDisplayName';

    const strategy = displayTextStrategies.get(strategyKey);
    return strategy ? strategy(item, column) : '';
  }

  private processFormatterResult(formatterResult: any, item: any): string {
    if (typeof formatterResult === 'object' && formatterResult.text) {
      return this.processBoldVariables(formatterResult, item);
    }
    if (typeof formatterResult === 'string') {
      return formatterResult;
    }
    return '';
  }

  processBoldVariables(formatterResult: { text: string; boldVariables: string[] }, item: any): string {
    let result = formatterResult.text;

    formatterResult.boldVariables.forEach(variableName => {
      const variableValue = this.lodashGet(item, variableName);
      if (variableValue) {
        result = result.replace(variableValue, `<strong>${variableValue}</strong>`);
      }
    });

    return result;
  }

  shouldUseInnerHtml(column: AppTableColumn, displayText?: string): boolean {
    return displayText?.includes('<strong>') ?? false;
  }

  getMainCellDisplayText(item: any, column: AppTableColumn): string {
    if (column.moreTag?.displayNameProp) {
      return this.lodashGet(item, column.moreTag.displayNameProp, column.id.toString());
    }

    if (column.moreTag?.listItemFormatter) {
      const formatterResult = column.moreTag.listItemFormatter(item);
      if (typeof formatterResult === 'object' && formatterResult.text) {
        return this.processBoldVariables(formatterResult, item);
      }
      return formatterResult as string;
    }

    return '';
  }

  getPopoverListStyle(moreTag: any): any {
    if (!moreTag?.maxVisibleItems) {
      return {};
    }

    const maxHeight = (moreTag.maxVisibleItems * 24) + 16;
    return {
      'max-height': `${maxHeight}px`,
      'overflow-y': 'auto',
      'overflow-x': 'hidden',
    };
  }

}
